import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  updatePayment,
  type AdminPayment,
  type UpdatePaymentPayload,
} from '../../../api/payments.api';
import { formatPlanMoney } from '../../../lib/planCurrency';

type Props = {
  open: boolean;
  payment: AdminPayment | null;
  onClose: () => void;
  onSaved: (payment: AdminPayment) => void;
};

export default function EditPaymentModal({ open, payment, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [amount, setAmount] = useState('');
  const [status, setStatus] = useState<'paid' | 'pending'>('pending');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !payment) return;
    setAmount(String(payment.amount ?? ''));
    setStatus(payment.status === 'paid' ? 'paid' : 'pending');
    setReference(payment.reference ?? '');
    setNotes(payment.notes ?? '');
    setError('');
    setSaving(false);
  }, [open, payment]);

  if (!open || !payment) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsedAmount = Number(amount);
    if (!Number.isFinite(parsedAmount) || parsedAmount < 0) {
      setError(t('payments.edit.amount_invalid'));
      return;
    }

    setSaving(true);
    setError('');
    const payload: UpdatePaymentPayload = {
      amount: parsedAmount,
      status,
      reference: reference.trim() || null,
      notes: notes.trim() || null,
    };
    const result = await updatePayment(payment.id, payload);
    setSaving(false);
    if (result.ok === false) {
      setError(result.message);
      return;
    }
    onSaved(result.data);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">{t('payments.edit.title')}</h3>
            <p className="mt-0.5 text-xs text-gray-400">
              {payment.tenant_name ?? '—'} · {formatPlanMoney(payment.amount, payment.currency)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="space-y-4 px-5 py-4">
          {error ? (
            <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700 ring-1 ring-rose-100">
              {error}
            </div>
          ) : null}

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-600">
              {t('payments.edit.amount')}
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
              required
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-600">
              {t('payments.edit.status')}
            </label>
            <div className="grid grid-cols-2 gap-2">
              {(
                [
                  { value: 'paid' as const, labelKey: 'payments.edit.status_paid' },
                  { value: 'pending' as const, labelKey: 'payments.edit.status_unpaid' },
                ] as const
              ).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setStatus(opt.value)}
                  className={`rounded-lg px-3 py-2 text-sm font-semibold cursor-pointer ${
                    status === opt.value
                      ? opt.value === 'paid'
                        ? 'bg-emerald-600 text-white'
                        : 'bg-amber-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {t(opt.labelKey)}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-600">
              {t('payments.edit.reference')}
            </label>
            <input
              type="text"
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder={t('payments.edit.reference_placeholder')}
              className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-semibold text-gray-600">
              {t('payments.edit.notes')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder={t('payments.edit.notes_placeholder')}
              className="w-full resize-none rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/30"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 cursor-pointer"
            >
              {t('payments.edit.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-lg bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700 disabled:opacity-50 cursor-pointer"
            >
              {saving ? t('payments.edit.saving') : t('payments.edit.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
